import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check } from "lucide-react";

const Index = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-20">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-12">
              <div className="md:w-1/2 space-y-6">
                <h1 className="text-4xl md:text-5xl font-bold leading-tight">
                  Land More Interviews with AI-Optimized Resumes
                </h1>
                <p className="text-lg text-gray-600">
                  Our AI tailors your resume to match specific job descriptions, boosting your chances of getting past ATS systems and landing interviews.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/auth/signup">
                    <Button size="lg" className="w-full sm:w-auto">
                      Get Started
                    </Button>
                  </Link>
                  <Link to="/about">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Learn How It Works
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="md:w-1/2">
                <img 
                  src="https://images.unsplash.com/photo-1586282391129-76a6df230234?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80"
                  alt="Resume optimization" 
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* How It Works */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">How ResumeBoost Works</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Three simple steps to create a perfectly tailored resume for each job application
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="mb-6">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-100 text-brand-500 text-2xl font-bold">
                    1
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Upload Your Resume</h3>
                <p className="text-gray-600">
                  Upload your existing resume in PDF or DOCX format to our secure platform. We'll handle the rest while keeping your information safe and private.
                </p>
              </div>
              
              <div className="text-center">
                <div className="mb-6">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-100 text-brand-500 text-2xl font-bold">
                    2
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Add Job Description</h3>
                <p className="text-gray-600">
                  Paste the job description you're applying for. Our AI analyzes the requirements, skills, and keywords the employer is looking for.
                </p>
              </div>
              
              <div className="text-center">
                <div className="mb-6">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-100 text-brand-500 text-2xl font-bold">
                    3
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Get Optimized Resume</h3>
                <p className="text-gray-600">
                  Our AI customizes your resume by aligning your skills and experience with the job requirements, increasing your chances of landing an interview.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Benefits */}
        <section className="bg-gray-50 py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Why Choose ResumeBoost</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our AI-powered platform gives you the competitive edge in your job search
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-100 p-2 rounded-full">
                    <Check className="text-brand-500 h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">ATS Optimization</h3>
                    <p className="text-gray-600">
                      Our AI optimizes your resume for Applicant Tracking Systems, ensuring your application doesn't get filtered out.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-100 p-2 rounded-full">
                    <Check className="text-brand-500 h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Keyword Matching</h3>
                    <p className="text-gray-600">
                      We analyze job descriptions to include the right keywords and phrases that hiring managers are looking for.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-100 p-2 rounded-full">
                    <Check className="text-brand-500 h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Multiple Versions</h3>
                    <p className="text-gray-600">
                      Create tailored versions of your resume for different job applications, increasing your chances of success.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-100 p-2 rounded-full">
                    <Check className="text-brand-500 h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Affordable</h3>
                    <p className="text-gray-600">
                      At just $1 per customized resume, our service is an affordable investment in your job search success.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA */}
        <section className="bg-brand-500 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Land More Interviews?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Start optimizing your resume today and increase your chances of getting noticed.
            </p>
            <Link to="/auth/signup">
              <Button size="lg" variant="outline" className="bg-white text-brand-500 hover:bg-brand-50">
                Get Started Now
              </Button>
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Index;
